import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Sparkles, BarChart3, MessageSquare, Users, Users2, Rss, Heart, MapPin,
  Calendar, BookOpen, Bookmark, Repeat2, Quote, SlidersHorizontal, Shield, AtSign, Compass,
  Layers, Megaphone, UserPlus,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Reveal, Shot, Eyebrow } from "@/components/features/FeatureKit";
import { RankingsMock, KpiMock } from "@/components/features/mocks";

const PROFILE_CARDS = [
  {
    key: "personal-profiles",
    title: "Personal Profiles",
    desc: "Show who you are, what you know, and what you bring to the startup ecosystem.",
    cta: "Explore Personal Profiles",
    visual: <Shot src="/features/personal-profile.png" alt="A Startup Otter personal profile" url="startupotter.com/p/maya-chen" cropClass="h-[280px]" />,
  },
  {
    key: "business-profiles",
    title: "Business Profiles",
    desc: "Give your startup, company, or organization a home of its own.",
    cta: "Explore Business Profiles",
    visual: <Shot src="/features/business-profile.png" alt="A Startup Otter business profile for Anchor Legal Partners" url="startupotter.com/b/anchor-legal" cropClass="h-[280px]" />,
  },
  {
    key: "capital-profiles",
    title: "Capital Profiles",
    desc: "Build a capital presence designed for investors, funds, and allocators.",
    cta: "Explore Capital Profiles",
    visual: <Shot src="/features/capital-profile.png" alt="A Startup Otter capital profile for Launchpad Accelerator" url="startupotter.com/capital/launchpad-accelerator" cropClass="h-[280px]" />,
  },
];

const MINI = [
  { icon: MessageSquare, title: "Direct Messaging", desc: "Turn a friend into a private conversation." },
  { icon: Users, title: "Friend", desc: "Build your person-to-person startup network." },
  { icon: Rss, title: "Follow", desc: "Keep up with people and organizations that interest you." },
  { icon: Heart, title: "Fan", desc: "Show extra support for the startups, organizations, and people you believe in." },
  { icon: MapPin, title: "Local Mode", desc: "Bring the startup ecosystem closer to home." },
  { icon: MessageSquare, title: "Chatter", desc: "Share Requests, Questions, Offers, Updates, Insights, Work, and more." },
  { icon: Calendar, title: "Meeting Requests", desc: "Go from finding someone to actually talking." },
  { icon: Bookmark, title: "Bookmarks", desc: "Save the good stuff before it floats away." },
  { icon: Repeat2, title: "Re-Chat", desc: "Help useful conversations travel farther through the community." },
  { icon: Quote, title: "Quote Re-Chat", desc: "Share a post and add your own perspective." },
  { icon: SlidersHorizontal, title: "Tailored Feeds", desc: "Shape your feed around the people, industries, and activity that matter to you." },
  { icon: Shield, title: "Team Roles", desc: "Manage organizations with Owners, Admins, and Contributors." },
  { icon: AtSign, title: "@Handles", desc: "Give every identity a recognizable home across Startup Otter." },
  { icon: Compass, title: "Local Discovery", desc: "Find people, organizations, resources, events, and activity around you." },
];

const CardLink = ({ to, label, testId }) => (
  <Link to={to} className="mt-4 inline-flex items-center gap-1.5 text-sm font-[640] text-primary transition-colors hover:text-primary/80" data-testid={testId}>
    {label} <ArrowRight className="h-4 w-4" aria-hidden="true" />
  </Link>
);

export default function FeaturesIndex() {
  return (
    <div className="overflow-x-hidden" data-testid="features-page">
      {/* ============ HERO ============ */}
      <section
        className="border-b border-border"
        style={{ background: "radial-gradient(1100px 460px at 15% -20%, hsl(182 60% 88% / 0.9), transparent 60%), linear-gradient(160deg, #E7F6F6 0%, #F6FAFA 55%, #FFFFFF 100%)" }}
      >
        <div className="mx-auto max-w-4xl px-4 py-20 sm:px-6 lg:px-8 lg:py-24">
          <Reveal><Eyebrow icon={Sparkles}>Features</Eyebrow></Reveal>
          <Reveal delay={0.05}>
            <h1 className="mt-5 text-4xl font-[700] tracking-[-0.02em] text-foreground sm:text-5xl lg:text-6xl lg:leading-[1.05]">
              Built for a startup ecosystem that actually connects.
            </h1>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 max-w-2xl text-base leading-8 text-muted-foreground sm:text-lg">
              Startup Otter brings together the people, businesses, capital, events, resources, conversations,
              and opportunities that make startup communities work.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ============ THREE PROFILE CARDS ============ */}
      <section className="bg-background" data-testid="features-profiles">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            {PROFILE_CARDS.map((c, i) => (
              <Reveal key={c.key} delay={i * 0.08}>
                <Link
                  to={`/features/${c.key}`}
                  className="group flex h-full flex-col rounded-3xl border border-border bg-card p-6 transition-all duration-300 hover:-translate-y-1 hover:border-primary/30 hover:shadow-[0_24px_50px_-28px_rgba(16,24,40,0.35)]"
                  data-testid={`feature-card-${c.key}`}
                >
                  <div className="overflow-hidden rounded-2xl">{c.visual}</div>
                  <h2 className="mt-6 text-xl font-[680] text-foreground">{c.title}</h2>
                  <p className="mt-2 flex-1 text-sm leading-6 text-muted-foreground">{c.desc}</p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-[640] text-primary">
                    {c.cta} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============ EVENTS + RESOURCES ============ */}
      <section className="bg-card" data-testid="features-events-resources">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-6 px-4 py-16 sm:px-6 lg:grid-cols-2 lg:px-8 lg:py-20">
          {[
            { key: "events", icon: Calendar, title: "Events", desc: "Discover where the startup community is gathering—and the people behind it.", cta: "Explore Events" },
            { key: "resources", icon: BookOpen, title: "Resources", desc: "Find and share tools, knowledge, programs, and opportunities worth using.", cta: "Explore Resources" },
          ].map((c, i) => (
            <Reveal key={c.key} delay={i * 0.08}>
              <Link
                to={`/features/${c.key}`}
                className="group flex h-full items-start gap-5 rounded-3xl border border-border bg-background p-7 transition-all duration-300 hover:-translate-y-1 hover:border-primary/30 hover:shadow-[0_20px_44px_-28px_rgba(16,24,40,0.32)]"
                data-testid={`feature-card-${c.key}`}
              >
                <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                  <c.icon className="h-6 w-6" aria-hidden="true" />
                </span>
                <div>
                  <h2 className="text-xl font-[680] text-foreground">{c.title}</h2>
                  <p className="mt-2 text-sm leading-6 text-muted-foreground">{c.desc}</p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-[640] text-primary">
                    {c.cta} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ============ OTTER ASSIST (flagship) ============ */}
      <section className="relative overflow-hidden bg-[#0b1220] text-white" data-testid="features-otter-assist">
        <div className="pointer-events-none absolute -right-24 top-1/4 h-96 w-96 rounded-full bg-primary/25 blur-[110px]" />
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-4 py-20 sm:px-6 lg:grid-cols-2 lg:gap-16 lg:px-8 lg:py-24">
          <Reveal>
            <div>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white/[0.07] px-3 py-1 text-xs font-[600] uppercase tracking-[0.1em] text-teal-200 ring-1 ring-white/10">
                <Sparkles className="h-3.5 w-3.5" aria-hidden="true" /> Flagship
              </span>
              <h2 className="mt-5 text-3xl font-[700] tracking-[-0.01em] sm:text-4xl lg:text-5xl lg:leading-[1.1]">Otter Assist</h2>
              <p className="mt-5 max-w-lg text-base leading-8 text-white/75">
                Tell us what you need. Otter Assist helps surface people across the community who may actually
                be able to help.
              </p>
              <p className="mt-4 text-sm font-[600] text-teal-300">The AI does the swimming. Humans make the connection.</p>
              <Link to="/features/otter-assist">
                <Button size="lg" className="mt-8 rounded-full bg-white px-8 text-primary hover:bg-white/90" data-testid="feature-card-otter-assist">
                  Meet Otter Assist <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
                </Button>
              </Link>
            </div>
          </Reveal>
          <Reveal delay={0.12} y={30}><Shot src="/features/otter-assist-needs.png" alt="Community Needs delivered by Otter Assist, with why you matched and I Can Help" url="startupotter.com/dashboard/otter-assist" testId="features-otter-assist-visual" /></Reveal>
        </div>
      </section>

      {/* ============ ANALYTICS ============ */}
      <section className="bg-background" data-testid="features-analytics">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-24">
          <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-16">
            <Reveal y={30} className="lg:order-2">
              <RankingsMock testId="features-analytics-visual" />
            </Reveal>
            <Reveal delay={0.08} className="lg:order-1">
              <div>
                <Eyebrow icon={BarChart3}>Analytics</Eyebrow>
                <h2 className="mt-5 text-3xl font-[700] tracking-[-0.01em] text-foreground sm:text-4xl lg:leading-[1.12]">
                  Startup Otter Analytics
                </h2>
                <p className="mt-5 max-w-lg text-base leading-8 text-muted-foreground">
                  See how people discover, engage with, and act on everything you manage across Startup Otter.
                </p>
                <div className="mt-7">
                  <KpiMock
                    testId="features-analytics-kpis"
                    items={[
                      { label: "Unique audience", value: "842", delta: "+7%" },
                      { label: "Entity visits", value: "1,914", delta: "+12%" },
                      { label: "Engagement", value: "268", delta: "+4%" },
                      { label: "Followers gained", value: "36", delta: "+9%" },
                    ]}
                  />
                </div>
                <Link to="/features/analytics">
                  <Button size="lg" className="mt-8 rounded-full px-8" data-testid="feature-card-analytics">
                    Explore Analytics <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
                  </Button>
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ============ GROUPS ============ */}
      <section className="border-t border-border bg-card" data-testid="features-groups">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-24">
          <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-16">
            <Reveal y={30}>
              <Shot src="/features/groups-directory.png" alt="Browsing Startup Otter groups with filters for category, industry and distance" url="startupotter.com/groups" cropClass="h-[380px]" testId="features-groups-visual" />
            </Reveal>
            <Reveal delay={0.08}>
              <div>
                <Eyebrow icon={Users2}>Groups</Eyebrow>
                <h2 className="mt-5 text-3xl font-[700] tracking-[-0.01em] text-foreground sm:text-4xl lg:leading-[1.12]">
                  Startup Otter Groups
                </h2>
                <p className="mt-5 max-w-lg text-base leading-8 text-muted-foreground">
                  Give your community a home of its own. Groups bring members, streams,
                  announcements, events, and private conversation together — so the momentum
                  from one gathering carries into the next.
                </p>
                <ul className="mt-7 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {[
                    { icon: Layers, label: "Streams for each conversation" },
                    { icon: Megaphone, label: "Announcements that reach everyone" },
                    { icon: UserPlus, label: "Open, approval, or invite only" },
                    { icon: MapPin, label: "Discoverable near you" },
                  ].map((b) => (
                    <li key={b.label} className="flex items-start gap-2.5 text-sm leading-6 text-muted-foreground">
                      <span className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                        <b.icon className="h-3.5 w-3.5" aria-hidden="true" />
                      </span>
                      {b.label}
                    </li>
                  ))}
                </ul>
                <Link to="/features/groups">
                  <Button size="lg" className="mt-8 rounded-full px-8" data-testid="feature-card-groups">
                    Explore Groups <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
                  </Button>
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ============ MINI FEATURES ============ */}
      <section className="border-t border-border bg-card" data-testid="features-mini">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
          <Reveal>
            <h2 className="max-w-2xl text-2xl font-[680] tracking-[-0.01em] text-foreground sm:text-3xl">
              There&apos;s plenty more beneath the surface.
            </h2>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {MINI.map((m, i) => (
              <Reveal key={m.title} delay={Math.min(i * 0.03, 0.3)}>
                <div className="flex h-full items-start gap-3 rounded-2xl border border-border bg-background p-4 transition-colors hover:border-primary/30" data-testid={`mini-feature-${m.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                    <m.icon className="h-4 w-4" aria-hidden="true" />
                  </span>
                  <div>
                    <h3 className="text-sm font-[640] text-foreground">{m.title}</h3>
                    <p className="mt-1 text-sm leading-6 text-muted-foreground">{m.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal>
            <div className="mt-12 flex flex-col items-start gap-3 sm:flex-row sm:items-center">
              <Link to="/register">
                <Button size="lg" className="rounded-full px-8" data-testid="features-final-cta">
                  Join Startup Otter <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
                </Button>
              </Link>
              <CardLink to="/people" label="Take a look around the community" testId="features-secondary-cta" />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
